import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff } from "lucide-react";
import {
  getPageConfig,
  updatePageConfig,
} from "../../../../../Services/adminCorporates";
import amplitudeService from "../../../../../Services/amplitude";
import { Button } from "../../../../../ui/button";
import { Skeleton } from "../../../../../ui/skeleton";
import { cn } from "../../../../../lib/utils";
import { SECTION_DISPLAY_NAMES } from "./schemas";

// Sections that live in the reorderable middle column. CTA, stepper labels,
// and the footer are pinned slots on the activation page and not reorderable
// or hideable from this UI.
const REORDERABLE_KEYS = new Set([
  "hero",
  "what_this_is",
  "when_to_use",
  "where_it_fits",
  "private_by_design",
  "closing_tagline",
]);

const SortableRow = ({ sectionKey, hidden, onToggleHide }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sectionKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 border rounded-md bg-white",
        isDragging && "shadow-lg ring-2 ring-primary",
        hidden && "opacity-60"
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span
        className={cn(
          "flex-1 text-sm",
          hidden && "line-through text-muted-foreground"
        )}
      >
        {SECTION_DISPLAY_NAMES[sectionKey] || sectionKey}
      </span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        onClick={() => onToggleHide(sectionKey)}
        aria-label={hidden ? "Show section" : "Hide section"}
        title={hidden ? "Show on the public page" : "Hide on the public page"}
      >
        {hidden ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </button>
    </div>
  );
};

/**
 * Sheet body for editing per-corporate page layout: section ordering and
 * visibility for the reorderable middle column. Notifies the parent of
 * unsaved changes via `onLocalChange` so it can preview them.
 */
const LayoutEditor = ({
  pageKey,
  onSaved,
  onLocalChange,
  pinnedKeys = ["hero_image", "cta", "step_labels", "footer"],
}) => {
  const { id } = useParams();
  const sensors = useSensors(useSensor(PointerSensor));

  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedNotice, setSavedNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPageConfig(id, pageKey);
        if (cancelled) return;
        const fullOrder = Array.isArray(data?.section_order)
          ? data.section_order
          : [];
        const hiddenList = Array.isArray(data?.hidden_section_keys)
          ? data.hidden_section_keys
          : [];
        // Only show reorderable keys in the editor.
        const reorderable = fullOrder.filter((k) => REORDERABLE_KEYS.has(k));
        setOrder(reorderable);
        setHidden(new Set(hiddenList.filter((k) => REORDERABLE_KEYS.has(k))));
      } catch (err) {
        if (!cancelled)
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load layout"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, pageKey]);

  // Push local-only changes up so the preview reflects unsaved tweaks.
  useEffect(() => {
    if (!onLocalChange) return;
    onLocalChange({
      sectionOrder: [...order, ...pinnedKeys],
      hiddenKeys: Array.from(hidden),
    });
  }, [order, hidden, onLocalChange, pinnedKeys]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setOrder((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleToggleHide = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSavedNotice(null);
    setError(null);
    try {
      const fullOrder = [...order, ...pinnedKeys];
      const hiddenList = Array.from(hidden);
      await updatePageConfig(id, pageKey, {
        section_order: fullOrder,
        hidden_section_keys: hiddenList,
      });
      amplitudeService.track?.("admin_page_layout_saved", {
        corporate_id: id,
        page_key: pageKey,
        hidden_count: hiddenList.length,
      });
      setSavedNotice("Layout saved.");
      onSaved?.({ sectionOrder: fullOrder, hiddenKeys: hiddenList });
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to save layout"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Drag to reorder. Use the eye toggle to hide a section from the public
        page. CTA, stepper, and footer are pinned and can't be reordered.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {order.map((key) => (
              <SortableRow
                key={key}
                sectionKey={key}
                hidden={hidden.has(key)}
                onToggleHide={handleToggleHide}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {savedNotice && (
          <span className="text-sm text-emerald-600 mr-auto">{savedNotice}</span>
        )}
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save layout"}
        </Button>
      </div>
    </div>
  );
};

export default LayoutEditor;
